// src/pages/FacturacionPresupuestos/DocumentSidebarToolbar.js
import React, { useState } from 'react';
import { 
    FaChevronRight, 
    FaChevronLeft, 
    FaFont, 
    FaPalette, 
    FaImage, 
    FaBook, 
    FaPaperPlane, 
    FaBold, 
    FaItalic, 
    FaUnderline, 
    FaAlignLeft, 
    FaAlignCenter, 
    FaAlignRight,
    FaPlus,
    FaWhatsapp,
    FaEnvelope,
    FaSave,
    FaUpload
} from 'react-icons/fa';
import { TEMAS_ZEN, FUENTES_DISPONIBLES, TAMANOS_HOJA } from '../../utils/plantillasDocumentos';
import { actualizarSiglasEnFolio } from '../../utils/folioFacturacionUtils';
import SectionFontSizeControl from './SectionFontSizeControl';
import './DocumentSidebarToolbar.css';

const DocumentSidebarToolbar = ({
    documento,
    setDocumento,
    onGuardar,
    onAbrirSendGrid,
    onCompartirWhatsApp,
    clientesGuardados = [],
    conceptosGuardados = [],
    guardando = false,
    logosDisponibles = [],
    firmasDisponibles = [],
    onSubirLogo,
    onSubirFirma
}) => {
    const [collapsed, setCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState('diseno'); // 'estilo', 'diseno', 'logos', 'catalogos', 'envio'
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [busquedaConcepto, setBusquedaConcepto] = useState('');

    // Actualizar campo directo
    const handleUpdateField = (field, value) => {
        setDocumento(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Actualizar estilo
    const handleToggleStyle = (styleProp, valueOn, valueOff = '') => {
        const actual = documento.estilos?.[styleProp] || '';
        const nuevoValor = actual === valueOn ? valueOff : valueOn;
        setDocumento(prev => ({
            ...prev,
            estilos: {
                ...prev.estilos,
                [styleProp]: nuevoValor
            }
        }));
    };

    // Cargar cliente seleccionado en el documento y actualizar siglas del folio
    const handleSeleccionarCliente = (cli) => {
        setDocumento(prev => {
            const nuevoFolio = actualizarSiglasEnFolio(prev.folio, cli, prev.tipoDocumento);
            return {
                ...prev,
                folio: nuevoFolio,
                cliente: {
                    ...prev.cliente,
                    nombre: cli.nombre || '',
                    rfc: cli.rfc || '',
                    contacto: cli.contacto || cli.atencionA || '',
                    telefono: cli.telefono || '',
                    email: cli.email || '',
                    direccion: cli.direccion || ''
                }
            };
        });
    };

    // Agregar concepto seleccionado a las partidas del documento
    const handleAgregarConcepto = (conc) => {
        setDocumento(prev => {
            const nuevaPartida = {
                id: Date.now().toString(),
                cantidad: 1,
                unidad: conc.unidad || 'Servicio',
                descripcion: conc.descripcion || conc.nombre || '',
                precioUnitario: Number(conc.precioUnitario) || 0
            };
            return {
                ...prev,
                conceptos: [...(prev.conceptos || []), nuevaPartida]
            };
        });
    };

    // Filtros de búsqueda
    const clientesFiltrados = clientesGuardados.filter(c => 
        (c.nombre || '').toLowerCase().includes(busquedaCliente.toLowerCase()) ||
        (c.rfc || '').toLowerCase().includes(busquedaCliente.toLowerCase())
    );

    const conceptosFiltrados = conceptosGuardados.filter(c => 
        (c.nombre || c.descripcion || '').toLowerCase().includes(busquedaConcepto.toLowerCase())
    );

    return (
        <aside className={`document-sidebar-container ${collapsed ? 'collapsed' : ''}`}>
            {/* Botón flotante para ocultar/mostrar */}
            <button 
                type="button"
                className="sidebar-toggle-btn"
                onClick={() => setCollapsed(!collapsed)}
                title={collapsed ? "Mostrar barra de herramientas" : "Ocultar barra de herramientas (Modo Pantalla Completa)"}
            >
                {collapsed ? <FaChevronLeft /> : <FaChevronRight />}
            </button>

            <div className="document-sidebar-panel">
                {/* Encabezado */}
                <div className="sidebar-header">
                    <h3 className="sidebar-title">
                        <FaPalette style={{ color: '#2b5329' }} /> Personalización Zen
                    </h3>
                </div>

                {/* Navegación de pestañas */}
                <div className="sidebar-tabs-nav">
                    <button 
                        type="button" 
                        className={`sidebar-tab-btn ${activeTab === 'diseno' ? 'active' : ''}`}
                        onClick={() => setActiveTab('diseno')}
                    >
                        <FaPalette />
                        <span>Diseño</span>
                    </button>
                    <button 
                        type="button" 
                        className={`sidebar-tab-btn ${activeTab === 'estilo' ? 'active' : ''}`}
                        onClick={() => setActiveTab('estilo')}
                    >
                        <FaFont />
                        <span>Tipografía</span>
                    </button>
                    <button 
                        type="button" 
                        className={`sidebar-tab-btn ${activeTab === 'logos' ? 'active' : ''}`}
                        onClick={() => setActiveTab('logos')}
                    >
                        <FaImage />
                        <span>Logos</span>
                    </button>
                    <button 
                        type="button" 
                        className={`sidebar-tab-btn ${activeTab === 'catalogos' ? 'active' : ''}`}
                        onClick={() => setActiveTab('catalogos')}
                    >
                        <FaBook />
                        <span>Catálogo</span>
                    </button>
                    <button 
                        type="button" 
                        className={`sidebar-tab-btn ${activeTab === 'envio' ? 'active' : ''}`}
                        onClick={() => setActiveTab('envio')}
                    >
                        <FaPaperPlane />
                        <span>Envío</span>
                    </button>
                </div>

                {/* Contenido según pestaña activa */}
                <div className="sidebar-tab-content">
                    {/* 1. DISEÑO Y HOJA */}
                    {activeTab === 'diseno' && (
                        <>
                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Temas de Color Zen</label>
                                <div className="themes-grid">
                                    {Object.values(TEMAS_ZEN).map(tema => (
                                        <div 
                                            key={tema.id}
                                            className={`theme-card-option ${documento.temaId === tema.id ? 'active' : ''}`}
                                            onClick={() => handleUpdateField('temaId', tema.id)}
                                        >
                                            <div className="theme-color-swatches">
                                                <span className="swatch-circle" style={{ background: tema.primario }} />
                                                <span className="swatch-circle" style={{ background: tema.secundario }} />
                                                <span className="swatch-circle" style={{ background: tema.acento }} />
                                            </div>
                                            <div className="theme-option-info">
                                                <p className="theme-option-name">{tema.nombre}</p>
                                                <p className="theme-option-sub">{tema.descripcion}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Tamaño de Hoja de Impresión</label>
                                <div className="sheet-size-buttons">
                                    {Object.values(TAMANOS_HOJA).map(tam => (
                                        <button 
                                            key={tam.id}
                                            type="button"
                                            className={`sheet-size-btn ${documento.tamanoHoja === tam.id ? 'active' : ''}`}
                                            onClick={() => handleUpdateField('tamanoHoja', tam.id)}
                                        >
                                            {tam.nombre}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Ajuste de Impresión / Escala</label>
                                <div className="sheet-size-buttons">
                                    <button
                                        type="button"
                                        className={`sheet-size-btn ${(documento.ajusteImpresion === 'ajustar_1_hoja' || !documento.ajusteImpresion) ? 'active' : ''}`}
                                        onClick={() => handleUpdateField('ajusteImpresion', 'ajustar_1_hoja')}
                                        title="Compacta márgenes y espaciado para que todo quepa en 1 sola hoja de papel"
                                    >
                                        ⚡ Ajustar a 1 Hoja
                                    </button>
                                    <button
                                        type="button"
                                        className={`sheet-size-btn ${documento.ajusteImpresion === 'estandar' ? 'active' : ''}`}
                                        onClick={() => handleUpdateField('ajusteImpresion', 'estandar')}
                                        title="Espaciado estándar con paginación libre"
                                    >
                                        📑 Flujo Estándar
                                    </button>
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Opciones de Impuestos (IVA)</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <input 
                                        type="checkbox" 
                                        id="chkAplicaIva"
                                        checked={documento.aplicaIva || false}
                                        onChange={(e) => handleUpdateField('aplicaIva', e.target.checked)}
                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                    />
                                    <label htmlFor="chkAplicaIva" style={{ fontSize: '13px', color: '#1e293b', cursor: 'pointer' }}>
                                        Desglosar IVA (16% MXN)
                                    </label>
                                </div>
                            </div>
                        </>
                    )}

                    {/* 2. TIPOGRAFÍA Y FORMATO */}
                    {activeTab === 'estilo' && (
                        <>
                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Familia Tipográfica</label>
                                <select 
                                    className="font-select"
                                    value={documento.fuenteId || 'montserrat'}
                                    onChange={(e) => handleUpdateField('fuenteId', e.target.value)}
                                >
                                    {FUENTES_DISPONIBLES.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.nombre} ({f.categoria})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Estilo de Texto</label>
                                <div className="format-buttons-row">
                                    <button 
                                        type="button"
                                        className={`format-btn ${documento.estilos?.fontWeight === 'bold' ? 'active' : ''}`}
                                        onClick={() => handleToggleStyle('fontWeight', 'bold', 'normal')}
                                        title="Negrita"
                                    >
                                        <FaBold />
                                    </button>
                                    <button 
                                        type="button"
                                        className={`format-btn ${documento.estilos?.fontStyle === 'italic' ? 'active' : ''}`}
                                        onClick={() => handleToggleStyle('fontStyle', 'italic', 'normal')}
                                        title="Cursiva"
                                    >
                                        <FaItalic />
                                    </button>
                                    <button 
                                        type="button"
                                        className={`format-btn ${documento.estilos?.textDecoration === 'underline' ? 'active' : ''}`}
                                        onClick={() => handleToggleStyle('textDecoration', 'underline', 'none')}
                                        title="Subrayado"
                                    >
                                        <FaUnderline />
                                    </button>
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Alineación Predeterminada</label>
                                <div className="format-buttons-row">
                                    <button 
                                        type="button"
                                        className={`format-btn ${documento.estilos?.textAlign === 'left' ? 'active' : ''}`}
                                        onClick={() => handleToggleStyle('textAlign', 'left')}
                                        title="Alinear a la izquierda"
                                    >
                                        <FaAlignLeft />
                                    </button>
                                    <button 
                                        type="button"
                                        className={`format-btn ${documento.estilos?.textAlign === 'center' ? 'active' : ''}`}
                                        onClick={() => handleToggleStyle('textAlign', 'center')}
                                        title="Centrar"
                                    >
                                        <FaAlignCenter />
                                    </button>
                                    <button 
                                        type="button"
                                        className={`format-btn ${documento.estilos?.textAlign === 'right' ? 'active' : ''}`}
                                        onClick={() => handleToggleStyle('textAlign', 'right')}
                                        title="Alinear a la derecha"
                                    >
                                        <FaAlignRight />
                                    </button>
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Tamaño de Texto por Sección</label>
                                <SectionFontSizeControl documento={documento} setDocumento={setDocumento} mode="bar" />
                            </div>
                        </>
                    )}

                    {/* 3. LOGOS Y FIRMAS */}
                    {activeTab === 'logos' && (
                        <>
                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Logo Principal (Empresa)</label>
                                <div className="logos-library-grid">
                                    {logosDisponibles.slice(0, 3).map((logo, idx) => (
                                        <div 
                                            key={idx}
                                            className={`logo-slot ${documento.emisor?.logoUrl === logo.url ? 'selected' : ''}`}
                                            onClick={() => setDocumento(prev => ({
                                                ...prev,
                                                emisor: { ...prev.emisor, logoUrl: logo.url }
                                            }))}
                                            title={logo.nombre || `Logo ${idx + 1}`}
                                        >
                                            <img src={logo.url} alt={`Logo ${idx + 1}`} />
                                        </div>
                                    ))}
                                    {logosDisponibles.length < 3 && (
                                        <label className="logo-slot" style={{ cursor: 'pointer' }}>
                                            <FaUpload style={{ color: '#94a3b8', marginBottom: '4px' }} />
                                            <span className="logo-slot-empty">Subir Logo</span>
                                            <input 
                                                type="file" 
                                                accept="image/*" 
                                                style={{ display: 'none' }}
                                                onChange={onSubirLogo}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Logo Secundario / Certificación</label>
                                <div className="logos-library-grid">
                                    {logosDisponibles.slice(0, 3).map((logo, idx) => (
                                        <div 
                                            key={idx}
                                            className={`logo-slot ${documento.emisor?.logoSecundarioUrl === logo.url ? 'selected' : ''}`}
                                            onClick={() => setDocumento(prev => ({
                                                ...prev,
                                                emisor: { ...prev.emisor, logoSecundarioUrl: logo.url }
                                            }))}
                                            title={logo.nombre || `Logo ${idx + 1}`}
                                        >
                                            <img src={logo.url} alt={`Logo ${idx + 1}`} />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Firmas y Sellos Autorizados</label>
                                <div className="logos-library-grid">
                                    {firmasDisponibles.slice(0, 3).map((firma, idx) => (
                                        <div 
                                            key={idx}
                                            className={`logo-slot ${(documento.firmas?.emisorFirmaUrl === firma.url || documento.firmaUrl === firma.url) ? 'selected' : ''}`}
                                            onClick={() => setDocumento(prev => ({
                                                ...prev,
                                                firmaUrl: firma.url,
                                                firmas: {
                                                    ...(prev.firmas || {}),
                                                    emisorFirmaUrl: firma.url
                                                }
                                            }))}
                                            title={firma.nombre || `Firma ${idx + 1}`}
                                        >
                                            <img src={firma.url} alt={`Firma ${idx + 1}`} />
                                        </div>
                                    ))}
                                    {firmasDisponibles.length < 3 && (
                                        <label className="logo-slot" style={{ cursor: 'pointer' }}>
                                            <FaUpload style={{ color: '#94a3b8', marginBottom: '4px' }} />
                                            <span className="logo-slot-empty">Subir Firma</span>
                                            <input 
                                                type="file" 
                                                accept="image/*" 
                                                style={{ display: 'none' }}
                                                onChange={onSubirFirma}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* 4. CATÁLOGO RÁPIDO */}
                    {activeTab === 'catalogos' && (
                        <>
                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Clientes Guardados</label>
                                <input 
                                    type="text"
                                    className="search-input-catalog"
                                    placeholder="Buscar cliente por nombre o RFC..."
                                    value={busquedaCliente}
                                    onChange={(e) => setBusquedaCliente(e.target.value)}
                                />
                                <div className="catalog-items-list">
                                    {clientesFiltrados.length === 0 ? (
                                        <div style={{ fontSize: '11px', color: '#94a3b8', padding: '8px' }}>
                                            No se encontraron clientes registrados.
                                        </div>
                                    ) : (
                                        clientesFiltrados.map((cli, idx) => (
                                            <div 
                                                key={cli.id || idx}
                                                className="catalog-item-card"
                                                onClick={() => handleSeleccionarCliente(cli)}
                                                title="Hacer clic para cargar este cliente en el documento"
                                            >
                                                <div>
                                                    <div className="catalog-item-name">{cli.nombre}</div>
                                                    <div className="catalog-item-detail">{cli.rfc || cli.telefono || 'Sin RFC'}</div>
                                                </div>
                                                <button type="button" className="btn-add-concept">Cargar</button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>

                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Conceptos Frecuentes</label>
                                <input 
                                    type="text"
                                    className="search-input-catalog"
                                    placeholder="Buscar concepto o partida..."
                                    value={busquedaConcepto}
                                    onChange={(e) => setBusquedaConcepto(e.target.value)}
                                />
                                <div className="catalog-items-list">
                                    {conceptosFiltrados.length === 0 ? (
                                        <div style={{ fontSize: '11px', color: '#94a3b8', padding: '8px' }}>
                                            No se encontraron conceptos en el catálogo.
                                        </div>
                                    ) : (
                                        conceptosFiltrados.map((conc, idx) => (
                                            <div 
                                                key={conc.id || idx}
                                                className="catalog-item-card"
                                                onClick={() => handleAgregarConcepto(conc)}
                                                title="Hacer clic para agregar este concepto al presupuesto"
                                            >
                                                <div>
                                                    <div className="catalog-item-name">{conc.nombre || conc.descripcion}</div>
                                                    <div className="catalog-item-detail">
                                                        ${(Number(conc.precioUnitario) || 0).toLocaleString('es-MX')} / {conc.unidad || 'Serv'}
                                                    </div>
                                                </div>
                                                <button type="button" className="btn-add-concept">
                                                    <FaPlus />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {/* 5. ENVÍO Y ACCIONES */}
                    {activeTab === 'envio' && (
                        <>
                            <div className="toolbar-group">
                                <label className="toolbar-group-label">Canales de Notificación</label>
                                <button 
                                    type="button"
                                    className="sidebar-action-btn sendgrid"
                                    onClick={onAbrirSendGrid}
                                >
                                    <FaEnvelope /> Enviar por Correo
                                </button>
                                <button 
                                    type="button"
                                    className="sidebar-action-btn whatsapp"
                                    onClick={onCompartirWhatsApp}
                                >
                                    <FaWhatsapp /> Compartir por WhatsApp
                                </button>
                                <button 
                                    type="button"
                                    className="sidebar-action-btn save"
                                    onClick={onGuardar}
                                    disabled={guardando}
                                >
                                    <FaSave /> {guardando ? 'Guardando...' : 'Guardar Documento'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </aside>
    );
};

export default DocumentSidebarToolbar;
